import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Star } from "lucide-react";
import { api } from "@/lib/api";
import { adminInput, AdminField, SaveBar } from "./fields";

const EMPTY = { name: "", location: "", rating: 5, text: "", source: "Direct", approved: false };

export const TestimonialsTab = () => {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/testimonials").then(async (pub) => {
    setItems(pub.data);
  }).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === "new") await api.post("/admin/testimonials", form);
      else await api.put(`/admin/testimonials/${editing}`, form);
      setEditing(null);
      setForm(EMPTY);
      load();
    } finally { setSaving(false); }
  };

  const remove = async (tm) => {
    if (!window.confirm(`Delete testimonial from ${tm.name}?`)) return;
    await api.delete(`/admin/testimonials/${tm.id}`);
    load();
  };

  if (items === null) return <p className="text-sm text-charcoal/50">Loading…</p>;

  if (editing !== null) {
    return (
      <form onSubmit={save} className="max-w-2xl space-y-4 rounded-2xl border border-charcoal/10 bg-white p-7" data-testid="testimonial-form">
        <div className="grid gap-4 sm:grid-cols-2">
          <AdminField label="Customer name *">
            <input required className={adminInput} data-testid="testimonial-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </AdminField>
          <AdminField label="Location / project">
            <input className={adminInput} value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
          </AdminField>
          <AdminField label="Rating">
            <select className={adminInput} value={form.rating} onChange={(e) => setForm({ ...form, rating: Number(e.target.value) })}>
              {[5, 4, 3, 2, 1].map((r) => <option key={r} value={r}>{r} stars</option>)}
            </select>
          </AdminField>
          <AdminField label="Source">
            <select className={adminInput} value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })}>
              {["Direct", "Google"].map((s) => <option key={s}>{s}</option>)}
            </select>
          </AdminField>
        </div>
        <AdminField label="Review text *">
          <textarea required className={`${adminInput} min-h-[100px]`} data-testid="testimonial-text-input" value={form.text} onChange={(e) => setForm({ ...form, text: e.target.value })} />
        </AdminField>
        <label className="flex items-center gap-2 text-sm text-charcoal/70">
          <input type="checkbox" checked={form.approved} onChange={(e) => setForm({ ...form, approved: e.target.checked })} data-testid="testimonial-approved-checkbox" />
          Approved — show on website
        </label>
        <SaveBar saving={saving} onCancel={() => { setEditing(null); setForm(EMPTY); }} />
      </form>
    );
  }

  return (
    <div>
      <button type="button" onClick={() => { setForm(EMPTY); setEditing("new"); }} data-testid="testimonial-new-button"
        className="mb-5 inline-flex items-center gap-2 rounded-full bg-charcoal px-5 py-2.5 text-xs font-bold text-gold">
        <Plus className="h-4 w-4" /> New Testimonial
      </button>
      {items.length === 0 ? (
        <p className="text-sm text-charcoal/50">No approved testimonials yet. Add real customer reviews here once collected — never invent them.</p>
      ) : (
        <div className="space-y-3">
          {items.map((tm) => (
            <div key={tm.id} className="flex items-center gap-4 rounded-xl border border-charcoal/10 bg-white px-5 py-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-bold text-charcoal">{tm.name} {tm.location ? <span className="font-normal text-charcoal/50">· {tm.location}</span> : null}</p>
                <p className="flex items-center gap-1 text-xs text-gold-dark">
                  {Array.from({ length: tm.rating }).map((_, i) => <Star key={i} className="h-3 w-3 fill-gold text-gold" />)}
                </p>
              </div>
              <button type="button" onClick={() => { setForm({ name: tm.name, location: tm.location || "", rating: tm.rating, text: tm.text, source: tm.source, approved: tm.approved }); setEditing(tm.id); }}
                className="rounded-full border border-charcoal/20 p-2 text-charcoal/60 hover:text-charcoal" aria-label="Edit testimonial">
                <Pencil className="h-4 w-4" />
              </button>
              <button type="button" onClick={() => remove(tm)}
                className="rounded-full border border-red-200 p-2 text-red-500 hover:text-red-700" aria-label="Delete testimonial">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
